import { StorageType, StorageOptions } from '@/types/storage';

/**
 * Получить storage объект по типу
 */
export const getStorage = (type: StorageType = 'localStorage'): Storage => {
  if (typeof window === 'undefined') {
    throw new Error('Storage is not available in server-side environment');
  }
  
  return type === 'localStorage' ? localStorage : sessionStorage;
};

/**
 * Безопасное получение значения из storage
 */
export const getStorageItem = <T = any>(
  key: string, 
  options: StorageOptions = {}
): T | null => {
  try {
    const storage = getStorage(options.storage);
    const item = storage.getItem(key);
    
    if (item === null) {
      return options.defaultValue ?? null;
    }
    
    if (options.deserialize) {
      return options.deserialize(item);
    }
    
    return JSON.parse(item);
  } catch (error) {
    console.error(`Error getting storage item "${key}":`, error);
    return options.defaultValue ?? null;
  }
};

/**
 * Безопасное сохранение значения в storage
 */
export const setStorageItem = <T = any>(
  key: string, 
  value: T, 
  options: StorageOptions = {}
): boolean => {
  try {
    const storage = getStorage(options.storage);
    
    if (options.serialize) {
      storage.setItem(key, options.serialize(value));
    } else {
      storage.setItem(key, JSON.stringify(value));
    }
    
    return true;
  } catch (error) {
    console.error(`Error setting storage item "${key}":`, error);
    return false;
  }
};

/**
 * Удаление значения из storage
 */
export const removeStorageItem = (
  key: string, 
  storage: StorageType = 'localStorage'
): boolean => {
  try {
    const storageObj = getStorage(storage);
    storageObj.removeItem(key);
    return true;
  } catch (error) {
    console.error(`Error removing storage item "${key}":`, error);
    return false;
  }
};

/**
 * Очистка всего storage
 */
export const clearStorage = (storage: StorageType = 'localStorage'): boolean => {
  try {
    const storageObj = getStorage(storage);
    storageObj.clear();
    return true;
  } catch (error) {
    console.error(`Error clearing storage:`, error);
    return false;
  }
};

/**
 * Получение всех ключей из storage
 */
export const getStorageKeys = (storage: StorageType = 'localStorage'): string[] => {
  try {
    const storageObj = getStorage(storage);
    return Object.keys(storageObj);
  } catch (error) {
    console.error(`Error getting storage keys:`, error);
    return [];
  }
};

/**
 * Проверка существования ключа в storage
 */
export const hasStorageItem = (
  key: string, 
  storage: StorageType = 'localStorage'
): boolean => {
  try {
    const storageObj = getStorage(storage);
    return storageObj.getItem(key) !== null;
  } catch (error) {
    console.error(`Error checking storage item "${key}":`, error);
    return false;
  }
};

/**
 * Получение размера storage в байтах
 */
export const getStorageSize = (storage: StorageType = 'localStorage'): number => {
  try {
    const storageObj = getStorage(storage);
    let total = 0;
    
    for (const key in storageObj) {
      if (storageObj.hasOwnProperty(key)) {
        total += storageObj[key].length + key.length;
      }
    }
    
    return total;
  } catch (error) {
    console.error(`Error getting storage size:`, error);
    return 0;
  }
};

/**
 * Создание префикса для ключей (namespace)
 */
export const createStorageNamespace = (namespace: string) => {
  const prefix = `${namespace}:`;
  
  return {
    get: <T = any>(key: string, options: StorageOptions = {}) => 
      getStorageItem<T>(`${prefix}${key}`, options),
    
    set: <T = any>(key: string, value: T, options: StorageOptions = {}) => 
      setStorageItem(`${prefix}${key}`, value, options),
    
    remove: (key: string, storage: StorageType = 'localStorage') => 
      removeStorageItem(`${prefix}${key}`, storage),
    
    has: (key: string, storage: StorageType = 'localStorage') => 
      hasStorageItem(`${prefix}${key}`, storage),
    
    clear: (storage: StorageType = 'localStorage') => {
      const storageObj = getStorage(storage);
      const keys = getStorageKeys(storage);
      const namespaceKeys = keys.filter(key => key.startsWith(prefix));
      
      namespaceKeys.forEach(key => storageObj.removeItem(key));
      return true;
    }
  };
};
